import type { MessageKey, Role } from '@ralph/contracts'
import { formatCount, formatList, pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/** The locale this file IS — see the note in the English sibling. */
const LOCALE = 'pt-BR'

type NotificationKey = Extract<MessageKey, `notifications.${string}`>

/** Quem é o público, nas palavras que a D-37 fixou. Nunca montado em um call site (D-20). */
const publicoDe = (audience: 'everyone' | 'groups', groups: string[]): string =>
  audience === 'everyone' ? 'todas as pessoas que entram' : formatList(LOCALE, groups)

/**
 * O particípio concorda com o que foi compartilhado, e é por isso que `item` existe.
 *
 * *O agente* é masculino e *a base de conhecimento* é feminina, então uma frase única com um
 * substantivo vindo como parâmetro seria impossível de escrever aqui — o inglês diz "shared"
 * para os dois e nunca precisa saber qual é (D-20, ADR-0018).
 */
const compartilhadoCom = (item: 'agent' | 'knowledge_base'): string =>
  item === 'agent' ? 'compartilhado' : 'compartilhada'

/**
 * Os quatro papéis como aparecem DENTRO de uma frase.
 *
 * Masculinos porque concordam com *o papel*, não com a pessoa que o tem — a mesma decisão que
 * `people.ts` documenta e pela mesma razão. Escritos aqui porque uma entrada deste mapa recebe
 * os PARÂMETROS e nada mais: não há catálogo no escopo para ler outro namespace.
 */
const ROLE_IN_SENTENCE: Record<Role, string> = {
  user: 'padrão',
  auditor: 'auditor',
  operator: 'operador',
  admin: 'administrador'
}

/**
 * O que uma notificação diz, em português do Brasil (conferido contra a forma inglesa em tempo
 * de compilação).
 *
 * A frase é escolhida quando alguém ABRE a notificação, na língua em que essa pessoa está
 * lendo naquele momento — e não na língua de quem provocou o evento. É por isso que a linha
 * guarda `subject_key` e `body_key` em vez de texto pronto (D-5 emendada, F-26).
 *
 * Nada aqui traduz o que a notificação NOMEIA: nomes de agentes, de bases, de grupos e de
 * pessoas, e as palavras que alguém digitou em uma nota, atravessam como foram escritos
 * (D-17).
 */
export const notifications: Pick<Messages['errors'], NotificationKey> = {
  // --- uma solicitação de compartilhamento, decidida ------------------------

  'notifications.sharing_decided_subject': ({ name, outcome, audience, groups, item }) =>
    outcome === 'approved'
      ? `“${name}” agora está ${compartilhadoCom(item)} com ${publicoDe(audience, groups)}`
      : `“${name}” não foi ${compartilhadoCom(item)} com ${publicoDe(audience, groups)}`,
  'notifications.sharing_approved_body': () =>
    'Já está valendo para essas pessoas, e as próximas versões chegam a elas sem passar por outra revisão.',
  'notifications.sharing_declined_body': ({ admin }) => `${admin} recusou o compartilhamento.`,
  'notifications.sharing_decided_note_body': ({ admin, outcome, note }) =>
    outcome === 'approved'
      ? `${admin} aprovou o compartilhamento: ${note}`
      : `${admin} recusou o compartilhamento: ${note}`,

  // --- uma solicitação que terminou sem decisão -----------------------------

  'notifications.sharing_request_closed_subject': ({ name }) =>
    `Sua solicitação para compartilhar “${name}” foi encerrada`,
  // Todo particípio aqui concorda com "solicitação", que é feminina — *encerrada*, *retirada*,
  // *substituída*. Um enum em inglês interpolado não produziria nenhum deles (D-20).
  'notifications.sharing_closed_body': ({ reason }) =>
    (
      ({
        group_deleted: 'Um grupo citado na solicitação foi excluído, então não havia mais o que aprovar. Nada mudou.',
        requester_lost_authority:
          'Foi encerrada porque quem pediu não mantém mais o que seria compartilhado. Nada mudou; quem mantém pode pedir de novo.',
        item_unavailable: 'Foi encerrada porque não é mais possível compartilhar do jeito que foi pedido. Nada mudou.',
        migrated:
          'Os grupos agora são gerenciados aqui, e os grupos citados na solicitação não existem mais. Nada mudou.'
      }) satisfies Record<typeof reason, string>
    )[reason],
  'notifications.sharing_closed_suspended_body': () =>
    'Um administrador suspendeu o agente, então não havia mais o que compartilhar. Nada mudou.',
  'notifications.sharing_closed_taken_down_body': ({ actor }) =>
    `${actor} tirou o agente do ar, então não havia mais o que compartilhar. Nada mudou.`,
  'notifications.sharing_closed_base_deleted_body': ({ actor }) =>
    `${actor} excluiu a base de conhecimento, então não havia mais o que compartilhar.`,
  'notifications.sharing_closed_group_deleted_subject': ({ group }) =>
    `Sua solicitação de compartilhamento foi encerrada: “${group}” foi excluído`,
  'notifications.sharing_closed_group_deleted_body': () =>
    'Nada mudou. Escolha de novo quem pode usar, se você ainda quiser compartilhar.',

  'notifications.sharing_request_withdrawn_subject': ({ name }) =>
    `Sua solicitação para compartilhar “${name}” foi retirada`,
  'notifications.sharing_request_withdrawn_body': ({ actor }) =>
    `${actor} retirou a solicitação. Nada mudou sobre quem pode usar.`,
  'notifications.sharing_request_replaced_subject': ({ name }) =>
    `Sua solicitação para compartilhar “${name}” foi substituída`,
  'notifications.sharing_request_replaced_body': ({ actor }) =>
    `${actor} pediu outra coisa antes de um administrador decidir. Agora é essa nova solicitação que está aguardando.`,

  // --- pedindo para compartilhar --------------------------------------------

  'notifications.sharing_requested_subject': ({ actor, name }) => `${actor} pediu para compartilhar “${name}”`,
  // Sujeito oculto, como o português faz: "Pediu para compartilhar…". O inglês precisa de
  // "They" porque não pode omitir o sujeito, e o demonstrativo concorda com o que foi pedido.
  'notifications.sharing_requested_body': ({ item, audience, groups }) =>
    item === 'agent'
      ? `Pediu para compartilhar este agente com ${publicoDe(audience, groups)}.`
      : `Pediu para compartilhar esta base de conhecimento com ${publicoDe(audience, groups)}.`,

  // --- compartilhado com um grupo -------------------------------------------

  'notifications.shared_into_group_subject': ({ name, group, item }) =>
    `“${name}” agora está ${compartilhadoCom(item)} com ${group}`,
  'notifications.shared_into_group_body': ({ actor, group, item }) =>
    item === 'agent'
      ? `${actor} compartilhou este agente com ${group}. Você está recebendo isso porque gerencia ${group}.`
      : `${actor} compartilhou esta base de conhecimento com ${group}. Você está recebendo isso porque gerencia ${group}.`,

  // --- emprestar uma base para um agente ------------------------------------

  'notifications.lending_requested_subject': ({ asker, agent, base }) =>
    `${asker} pediu para permitir que “${agent}” responda usando “${base}”`,
  'notifications.lending_requested_body': ({ agent, base }) =>
    `Se você permitir, todas as pessoas que podem usar “${agent}” recebem respostas de “${base}” por meio dele.`,
  'notifications.lending_allowed_subject': ({ agent, base }) => `Permitido: “${agent}” agora responde usando “${base}”`,
  'notifications.lending_allowed_body': ({ decider, agent }) =>
    `${decider} permitiu. Todas as pessoas que podem usar “${agent}” recebem essas respostas por meio dele.`,
  'notifications.lending_declined_subject': ({ agent, base }) =>
    `Recusado: “${agent}” não vai responder usando “${base}”`,
  'notifications.lending_declined_body': ({ decider }) => `${decider} não permitiu.`,
  'notifications.lending_decided_note_body': ({ decider, note }) => `${decider}: “${note}”`,
  // *Encerrada* concorda com a solicitação, *excluída* com a base: duas palavras, dois gêneros,
  // uma frase que o inglês escreve com "Closed" e "deleted" sem escolher nada.
  'notifications.lending_closed_base_deleted_subject': ({ base }) => `Encerrada: “${base}” foi excluída`,
  'notifications.lending_closed_base_deleted_body': ({ actor, base, agent }) =>
    `${actor} excluiu a base de conhecimento “${base}”, então “${agent}” não pode mais responder com esse ` +
    'material. Sua solicitação foi encerrada.',
  'notifications.lending_closed_group_deleted_subject': ({ group }) => `Encerrada: “${group}” foi excluído`,
  'notifications.lending_closed_group_deleted_body': ({ actor, base, agent }) =>
    `${actor} excluiu o grupo que mantinha “${base}”, então sua solicitação para que “${agent}” respondesse ` +
    'usando essa base foi encerrada. Fale com quem mantém a base agora, se você ainda precisar disso.',

  // --- a saúde de um agente -------------------------------------------------

  'notifications.agent_needs_attention_subject': () => 'Um dos seus agentes precisa de atenção',
  // Nunca `count === 1`: `Intl.PluralRules('pt-BR').select(0)` devolve `'one'`, então o zero
  // cai no singular nesta língua e não na outra (D-19).
  'notifications.agent_needs_attention_body': ({ count }) =>
    pluralCategory(LOCALE, count) === 'one'
      ? `${formatCount(LOCALE, count)} ponto precisa de atenção. Abra o agente para ver o que mudou.`
      : `${formatCount(LOCALE, count)} pontos precisam de atenção. Abra o agente para ver o que mudou.`,
  'notifications.agent_lost_base_subject': ({ agent }) => `“${agent}” perdeu uma das bases de conhecimento dele`,
  'notifications.agent_lost_base_body': ({ actor, base, agent }) =>
    `${actor} excluiu a base de conhecimento “${base}”. “${agent}” continua funcionando, mas não pode mais ` +
    'responder com esse material. Abra o construtor para anexar outra coisa.',

  // --- o anúncio do agente --------------------------------------------------

  'notifications.agent_suspended_subject': ({ name }) => `“${name}” foi suspenso`,
  'notifications.ownership_transferred_subject': ({ name, owner }) => `“${name}” agora pertence a ${owner}`,
  // "Quem é responsável", não "o dono": o novo dono pode ser uma pessoa de qualquer gênero ou
  // um grupo, e a frase não precisa saber qual (D-21).
  'notifications.ownership_transferred_body': () =>
    'Quem é responsável por um agente responde pelo que ele diz e faz. Quem pode usar não mudou.',
  'notifications.ownership_transferred_badge_body': () =>
    'O selo Oficial saiu junto com a mudança. Um administrador pode conceder de novo depois de revisar o agente.',
  'notifications.editor_invited_subject': ({ actor, agent }) =>
    `${actor} convidou você para ajudar a construir “${agent}”`,
  'notifications.editor_invited_body': () =>
    'Você pode aceitar ou recusar na sua lista de agentes. Nada muda até você decidir.',
  'notifications.question_shared_subject': ({ agent }) => `Alguém compartilhou uma pergunta com “${agent}”`,
  'notifications.question_shared_body': () =>
    'Abra a atividade do agente para ver o que as pessoas perguntaram e ele não respondeu bem.',

  // --- o que acontece com o acesso de uma pessoa ----------------------------

  'notifications.role_granted_subject': ({ actor, role }) => `${actor} deu a você o papel de ${ROLE_IN_SENTENCE[role]}`,
  'notifications.role_denied_subject': ({ actor, role }) => `${actor} removeu seu acesso de ${ROLE_IN_SENTENCE[role]}`,
  'notifications.role_revoked_subject': ({ actor, role }) => `${actor} removeu seu papel de ${ROLE_IN_SENTENCE[role]}`,
  'notifications.role_restored_subject': ({ actor, role }) =>
    `${actor} devolveu seu acesso de ${ROLE_IN_SENTENCE[role]}`,
  'notifications.role_approved_subject': ({ actor }) => `${actor} aprovou seu novo acesso`,
  'notifications.role_approved_body': () =>
    'Nesta instalação, um acesso de administrador precisa da aprovação de um segundo administrador.',
  'notifications.role_changed_body': () => 'Fale com um administrador se você não tiver certeza do que isso muda.',
  'notifications.role_revoked_body': () => 'Fale com um administrador se você precisar disso de volta.',
  'notifications.role_in_effect_body': () => 'Já está valendo.',

  // --- compartilhadas por várias das anteriores -----------------------------

  'notifications.their_note_body': ({ note }) => `Nota: “${note}”`
}
